/**
 * Upgrade modal — pops when /chat returns 402 (cap reached). Copy
 * branches on `gate_type` (the KIND of wall), then `window`:
 *
 * - `gate_type === "hard_wall"` → free user past their first leak report.
 *   No reset, no countdown — "you've earned your first report" + subscribe-
 *   to-continue ("See plans"). This is the free tier's only gate now.
 * - `gate_type === "soft_cap"` → a paid monthly cap was hit (Core 300 /
 *   Plus 1000). CTA nudges to a higher tier (or Unlimited for no caps),
 *   with a live reset countdown. `window === "daily"` is legacy/back-compat
 *   (the old free 10/day meter) and no longer produced for free users.
 *
 * Also exported as a context so any feature can trigger it without
 * re-wiring the component tree.
 *
 * Usage:
 *   - Wrap app tree in <UpgradeModalProvider>...</UpgradeModalProvider>
 *   - Anywhere in the tree: const { open } = useUpgradeModal()
 *     then call open({...}) when you catch CapReachedError.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { useResetCountdown } from "@/shared/resetCountdown";
import { Button } from "@/shared/components/Button";
import { ICON_STROKE, X } from "@/shared/components/icons";

interface UpgradeModalState {
  window: "daily" | "monthly" | "none";
  gateType: "soft_cap" | "hard_wall" | "none";
  subscriptionStatus: string;
  planLevel: string | null;
  used: number;
  limit: number;
}

interface UpgradeModalContextValue {
  /** Show the modal with the given cap-reached state. */
  open: (state: UpgradeModalState) => void;
  /** Hide the modal. */
  close: () => void;
}

const UpgradeModalContext = createContext<UpgradeModalContextValue | null>(null);

export function useUpgradeModal(): UpgradeModalContextValue {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) {
    throw new Error("useUpgradeModal must be used inside <UpgradeModalProvider>");
  }
  return ctx;
}

export function UpgradeModalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<UpgradeModalState | null>(null);

  const open = useCallback((s: UpgradeModalState) => setState(s), []);
  const close = useCallback(() => setState(null), []);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
      {state && <UpgradeModalView state={state} onClose={close} />}
    </UpgradeModalContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// The modal itself — purely presentational. Backdrop click closes it.
// ---------------------------------------------------------------------------

function UpgradeModalView({
  state,
  onClose,
}: {
  state: UpgradeModalState;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  // Hard wall has no reset — pass "none" so the countdown hook stays empty.
  const isHardWall = state.gateType === "hard_wall";
  const countdown = useResetCountdown(isHardWall ? "none" : state.window);
  const goToPricing = () => {
    onClose();
    navigate("/pricing");
  };

  // Copy variants: the free HARD WALL (post-first-report) vs a paid
  // SOFT CAP (resets). Free no longer has a daily window, but the
  // daily branch is kept for back-compat with any in-flight state.
  const isDaily = state.window === "daily";
  const eyebrow = isHardWall
    ? "First leak report unlocked"
    : isDaily
      ? "Daily limit reached"
      : "Monthly limit reached";
  const title = isHardWall
    ? "You've earned your first leak report 🎉"
    : isDaily
      ? `You've used today's ${state.limit} coach responses`
      : `You've used this month's ${state.limit} coach responses`;
  const body = isHardWall
    ? "That's the free tier — unlimited drilling all the way to your first leak report, and you're there. Subscribe to keep drilling with the coach and unlock ongoing reports as your game changes. Your saved report, session tracker, quizzes, and study sheets stay free."
    : isDaily
      ? "A coach response is each time the coach grades a decision or answers a question. Upgrade for a higher monthly limit — or go Unlimited to drill without caps."
      : "A coach response is each time the coach grades a decision or answers a question. You've reached this plan's monthly allowance. Upgrade for a higher monthly limit — or go Unlimited to drill without caps.";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[90dvh] flex flex-col rounded-2xl border border-border bg-surface relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center text-text-muted hover:text-text"
        >
          <X size={18} strokeWidth={ICON_STROKE} />
        </button>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-accent font-semibold mb-1">
              {eyebrow}
            </div>
            <h2 className="text-xl font-bold text-text tracking-tight">
              {title}
            </h2>
          </div>

          <p className="text-sm text-text-muted leading-relaxed">{body}</p>

          {/* Soft cap resets — show the countdown. Hard wall has no
              reset, so the countdown box is omitted entirely. */}
          {!isHardWall && (
            <div className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-sm">
              <span className="text-text-muted">Unlocks </span>
              <span className="font-mono tabular-nums text-accent">{countdown}</span>
              <span className="text-text-muted">: or instantly with an upgrade.</span>
            </div>
          )}
        </div>

        <div className="flex gap-2 p-6 pt-2 border-t border-border/40">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            {isHardWall ? "Not now" : isDaily ? "Wait it out" : "Maybe later"}
          </Button>
          <Button onClick={goToPricing} className="flex-1">
            {isHardWall ? "See plans" : "View pricing"}
          </Button>
        </div>
      </div>
    </div>
  );
}
